import React, { useState } from 'react';
import { useMasterData, useLocationCascade } from '../../hooks/useMasterData';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { LoadingSpinner } from '../../components/ui/Feedback';
import { Building2, Layers, Tag, ShieldAlert, CheckCircle2, Plus } from 'lucide-react';

export const MasterDataPage: React.FC = () => {
  const { plants, departments, categories, priorities, statuses, isLoading } = useMasterData();
  const [selectedPlant, setSelectedPlant] = useState<string>('PLT-001');
  const [selectedBuilding, setSelectedBuilding] = useState<string>('BLD-001');
  const [selectedFloor, setSelectedFloor] = useState<string>('FLR-001');
  const { buildings, floors, locations } = useLocationCascade(selectedPlant, selectedBuilding, selectedFloor);

  const [activeTab, setActiveTab] = useState<'locations' | 'departments' | 'categories' | 'workflow'>('locations');

  if (isLoading) return <LoadingSpinner size="lg" text="Loading Master Data Configurations..." />;

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-100">Master Data Configuration</h2>
          <p className="text-xs text-slate-400">Zero-hardcoding configuration manager for taxonomy, departments & SLAs</p>
        </div>
        <Button variant="primary" icon={<Plus className="w-4 h-4" />}>
          Add Master Record
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-800 gap-2">
        <button
          onClick={() => setActiveTab('locations')}
          className={`px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-colors flex items-center gap-2 ${
            activeTab === 'locations' ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <Building2 className="w-4 h-4" />
          Location Hierarchy
        </button>

        <button
          onClick={() => setActiveTab('departments')}
          className={`px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-colors flex items-center gap-2 ${
            activeTab === 'departments' ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <Layers className="w-4 h-4" />
          Departments ({departments.length})
        </button>

        <button
          onClick={() => setActiveTab('categories')}
          className={`px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-colors flex items-center gap-2 ${
            activeTab === 'categories' ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <Tag className="w-4 h-4" />
          Work Categories ({categories.length})
        </button>

        <button
          onClick={() => setActiveTab('workflow')}
          className={`px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-colors flex items-center gap-2 ${
            activeTab === 'workflow' ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <ShieldAlert className="w-4 h-4" />
          Priorities & SLA
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'locations' && (
        <Card title="5-Level Dynamic Location Cascade Demo">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">1. Plant</label>
              <select
                value={selectedPlant}
                onChange={e => { setSelectedPlant(e.target.value); setSelectedBuilding(''); setSelectedFloor(''); }}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2 text-xs text-slate-100"
              >
                {plants.map(p => (
                  <option key={p.plant_id} value={p.plant_id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">2. Building</label>
              <select
                value={selectedBuilding}
                onChange={e => { setSelectedBuilding(e.target.value); setSelectedFloor(''); }}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2 text-xs text-slate-100"
              >
                <option value="">Select Building</option>
                {buildings.map(b => (
                  <option key={b.building_id} value={b.building_id}>{b.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">3. Floor</label>
              <select
                value={selectedFloor}
                onChange={e => setSelectedFloor(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2 text-xs text-slate-100"
              >
                <option value="">Select Floor</option>
                {floors.map(f => (
                  <option key={f.floor_id} value={f.floor_id}>{f.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">4. Specific Location</label>
              <select className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2 text-xs text-slate-100">
                <option value="">Select Room/Bay</option>
                {locations.map(l => (
                  <option key={l.location_id} value={l.location_id}>{l.name} ({l.code})</option>
                ))}
              </select>
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'departments' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {departments.map(dept => (
            <Card key={dept.department_id} className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-slate-100 text-sm">{dept.name}</h4>
                <Badge label={dept.code} variant="info" />
              </div>
              <p className="text-[10px] font-mono text-slate-500">ID: {dept.department_id}</p>
            </Card>
          ))}
        </div>
      )}

      {activeTab === 'categories' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {categories.map(cat => (
            <Card key={cat.category_id} className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-slate-100 text-sm">{cat.name}</h4>
                <Badge label={cat.code} variant="warning" />
              </div>
              <p className="text-[10px] font-mono text-slate-500">ID: {cat.category_id}</p>
            </Card>
          ))}
        </div>
      )}

      {activeTab === 'workflow' && (
        <Card title="SLA Duration & Priority Matrix">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {priorities.map(prio => (
              <div key={prio.priority_id} className="p-4 bg-slate-900/60 border border-slate-800 rounded-xl space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm text-slate-100">{prio.name}</h4>
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: prio.color_code }}></span>
                </div>
                <p className="text-2xl font-semibold text-sky-400">{prio.sla_hours} hrs</p>
                <p className="text-[10px] text-slate-500">Target Resolution SLA</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
